/**
 * AD-031 — "Create project from this payment" (Increment C.3, Anil).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE OWNER RULING (2026-06-22): on a recorded payment, project creation is
 *  MANUAL-confirm but AUTO-SUGGESTED — a pre-filled "Create project from this
 *  payment" button (client + service + amount), confirmed by staff.
 *
 *  MONEY LANE (LOCKED, AD-003): there is ONE agreed price per engagement and
 *  ONE resolver (`resolveGateAmount`). This module DOES NOT invent a parallel
 *  money path — the paid amount becomes the project's AGREED PRICE via the
 *  canonical `setProjectAgreedPrice` (which seeds `gateAmounts` that
 *  `resolveGateAmount` reads). The pure layer here only SHAPES the suggestion +
 *  validates it; the impure orchestrator calls the existing project + money
 *  stores. No new pricing logic.
 *
 *  Firebase-free pure layer (the suggestion shaper + validator) for the `.cjs`
 *  runner; the impure `createProjectFromPayment` sits at the bottom.
 * ─────────────────────────────────────────────────────────────────────────
 */

import type { Tier, CoreTier } from './engagementTemplate';

/** A recorded/intended payment that could seed a project. */
export interface PaymentSuggestionInput {
  /** the paying client. */
  client: { clientId: string; clientNo: string; name: string };
  /** the service tier the payment is for (L1A / L2B …). Unknown → ''. */
  tier: Tier | string;
  /** the paid amount, in `currency` units (e.g. 11000 INR, or 40 USD). */
  amount: number;
  /** the engagement currency. */
  currency: string;
  /** optional project name; blank → the store's `<tier> — <client>` default. */
  projectName?: string;
  /** optional channel partner. */
  partnerId?: string | null;
}

/** The AUTO-SUGGESTED, pre-filled create-project form derived from a payment. */
export interface ProjectSuggestion {
  clientId: string;
  clientNo: string;
  clientName: string;
  tier: string;
  /** the agreed price to seed (== the paid amount). */
  agreedValue: number;
  currency: string;
  projectName: string;
  partnerId: string | null;
}

/**
 * PURE — shape the pre-filled suggestion from a payment. The agreed price is the
 * paid amount (the owner's "auto-suggested"). Staff may edit any field before
 * confirming; this is only the default.
 */
export function suggestProjectFromPayment(
  input: PaymentSuggestionInput,
): ProjectSuggestion {
  return {
    clientId: input.client.clientId,
    clientNo: input.client.clientNo,
    clientName: input.client.name,
    tier: (input.tier || '').toString().toUpperCase(),
    agreedValue: Number.isFinite(input.amount) ? input.amount : 0,
    currency: (input.currency || 'INR').toUpperCase(),
    projectName: input.projectName || '',
    partnerId: input.partnerId ?? null,
  };
}

/**
 * PURE — block creation only on the truly-required fields. A tier IS required
 * (the project's engagement template is keyed by it); a client is required; the
 * agreed value must be ≥ 0 (a 0 is allowed — a project can be created before any
 * money, but the suggestion normally carries the paid amount). Returns human
 * messages ([] == ok to create).
 */
export function validateProjectSuggestion(s: ProjectSuggestion): string[] {
  const errs: string[] = [];
  if (!(s.clientId || '').trim()) errs.push('A client is required.');
  if (!(s.tier || '').trim()) errs.push('Choose the service this payment is for.');
  if (!(s.agreedValue >= 0)) errs.push('The agreed amount must be a number.');
  return errs;
}

/**
 * PURE — the intake deep-link to OPEN after the project is created: the delivery
 * intake for the chosen tier, project-scoped (AD-030) so the answers land under
 * the new project (and carry the scoping answers forward, AD-031).
 */
export function deliveryIntakeLink(args: {
  tier: string;
  clientId: string;
  projectId: string;
}): string {
  const tier = encodeURIComponent(args.tier);
  const client = encodeURIComponent(args.clientId);
  const pid = encodeURIComponent(args.projectId);
  return `/intake?tier=${tier}&client=${client}&pid=${pid}`;
}

/* ─────────────────────────── impure orchestrator ─────────────────────────── */
/* Imports the project + money stores; the `.cjs` runner never loads this (it
 * imports only the pure exports above). */

import { createProjectForClient } from './projectStore';
import { setProjectAgreedPrice } from './recordPaymentStore';
import type { Currency } from './firms';

/**
 * Create the project from a confirmed suggestion, then set its AGREED PRICE to
 * the paid amount via the canonical `setProjectAgreedPrice` (the single money
 * resolver path — seeds `gateAmounts` that `resolveGateAmount` reads). Returns
 * the new project id + the delivery-intake deep-link to open.
 *
 * Order: create first (mints VP###), then seed the agreed price on that VP###.
 * The agreed-price seed is best-effort — if it fails (rare), the project still
 * exists at the standing rate and staff can set the price on the Payment tab.
 */
export async function createProjectFromPayment(
  s: ProjectSuggestion,
  actorName: string,
): Promise<{ projectId: string; clientNo: string; intakeLink: string }> {
  const res = await createProjectForClient(
    { clientId: s.clientId, clientNo: s.clientNo, name: s.clientName },
    s.tier as CoreTier,
    null, // sqft — not known from a payment; staff add it at delivery intake
    actorName,
    s.partnerId,
    s.projectName,
  );
  // Seed the agreed price = the paid amount (single money resolver path).
  try {
    await setProjectAgreedPrice(
      res.projectId,
      {
        tier: s.tier,
        currency: s.currency as Currency,
        value: s.agreedValue,
        prior: { currency: null, value: null }, // brand-new project, no prior
        note: 'Agreed price seeded from the recorded payment (Create project from payment).',
      },
      actorName,
    );
  } catch {
    // non-fatal — project exists at the standing rate; price settable later.
  }
  return {
    projectId: res.projectId,
    clientNo: res.clientNo,
    intakeLink: deliveryIntakeLink({
      tier: s.tier,
      clientId: s.clientId,
      projectId: res.projectId,
    }),
  };
}
